import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paymentAPI } from '../utils/api';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await paymentAPI.getStatus(sessionId);
      setPaymentData(response.data);
      setStatus(response.data.payment_status === 'paid' ? 'success' : 'pending');
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-primary/5"
        data-testid="payment-checking"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="text-center">
          <div className="spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-foreground/70">{t('checkout.success.confirming')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-primary/5 px-4" data-testid="payment-success-page">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 border border-slate-200 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" data-testid="success-icon" aria-hidden="true" />
          </div>

          <h1 className="text-3xl font-bold text-secondary mb-4">{t('checkout.success.title')}</h1>
          <p className="text-foreground/70 text-lg mb-8">{t('checkout.success.subtitle')}</p>

          {paymentData && (
            <div className="bg-slate-50 rounded-lg p-6 mb-8 text-left" data-testid="payment-details">
              <h3 className="font-semibold text-secondary mb-4">{t('checkout.paymentDetailsTitle')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-foreground/70">{t('checkout.amount')}:</span>
                  <span className="font-medium text-secondary">
                    {((paymentData.amount_total || 0) / 100).toFixed(2)} {(paymentData.currency || 'EUR').toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/70">{t('checkout.status')}:</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {t(`checkout.statuses.${paymentData.payment_status}.label`, { defaultValue: paymentData.payment_status })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/70">{t('checkout.success.reference')}:</span>
                  <span className="font-mono text-xs text-foreground/60">{(sessionId || '').slice(0, 20)}…</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-primary/5 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-secondary mb-3">{t('checkout.success.nextSteps')}</h3>
            <ul className="space-y-2 text-foreground/70">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{t('checkout.success.step2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{t('checkout.success.step1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{t('checkout.success.step4')}</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => navigate('/patient/dashboard')}
              className="flex-1 h-12 bg-primary hover:bg-primary/90 rounded-full"
              data-testid="go-to-dashboard-button"
            >
              {t('checkout.success.goToDashboard')}
              <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
            </Button>
            <Button
              onClick={() => navigate('/treatments')}
              variant="outline"
              className="flex-1 h-12 rounded-full"
              data-testid="browse-treatments-button"
            >
              {t('checkout.cancel.browseTreatments')}
            </Button>
          </div>

          <p className="text-sm text-foreground/60 mt-8">
            {t('checkout.success.supportHint')}
            <a href="mailto:support@treatx.health" className="text-primary hover:underline">support@treatx.health</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;